import { Listbox, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { Check, ChevronDown, Plus } from 'lucide-react';
import { Fragment, useState } from 'react';
import Button from '../Base/Button';
import { FormInput } from '../Base/Form';

interface RenderMultiSelectProps {
  label: string;
  options: string[];
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  allowCustomField?: boolean;
}

const MultiSelectInner = ({
  label,
  options,
  selected,
  setSelected,
  allowCustomField = false,
}: RenderMultiSelectProps) => {
  const [customInput, setCustomInput] = useState('');

  const allSelected =
    options.length > 0 && options.every((o) => selected.includes(o));

  const displayLabel = allSelected
    ? `All ${label}`
    : selected.length > 0
      ? `${selected.length} ${label}(s) selected`
      : `Select ${label}`;

  const addCustom = () => {
    const value = customInput.trim();
    if (!value || selected.includes(value)) return;
    setSelected((prev) => [...prev, value]);
    setCustomInput('');
  };

  return (
    <Listbox value={selected} onChange={setSelected} multiple>
      <div className='relative mt-2 col-span-2 z-100'>
        <Listbox.Button className='relative w-full cursor-default rounded-lg bg-white py-2 pl-3 pr-10 text-left text-gray-800 border col-span-2'>
          <span className='block truncate'>{displayLabel}</span>
          <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2'>
            <ChevronDown className='h-5 w-5 text-gray-500' />
          </span>
        </Listbox.Button>

        <Transition
          as={Fragment}
          leave='transition ease-in duration-100'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <Listbox.Options className='absolute z-50 mt-1 w-full overflow-auto rounded-md bg-white text-base shadow-md ring-1 ring-black/5 focus:outline-none sm:text-sm'>
            {allowCustomField && (
              <div className='flex items-center gap-2 p-2' key='custom-input'>
                <FormInput
                  type='text'
                  value={customInput}
                  placeholder={`Add custom ${label.toLowerCase()}…`}
                  onChange={(e) => setCustomInput(e.target.value)}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addCustom();
                    }
                  }}
                  onKeyUp={(e) => e.stopPropagation()}
                />
                <Button
                  type='button'
                  variant='soft-success'
                  disabled={!customInput.trim()}
                  className='flex items-center justify-center rounded-md'
                  onClick={addCustom}
                >
                  <Plus className='h-4 w-4' />
                </Button>
              </div>
            )}
            <div className='max-h-52 overflow-auto'>
              {[
                ...options,
                ...selected.filter((v) => !options.includes(v)),
              ].map((option) => (
                <Listbox.Option key={option} value={option} as={Fragment}>
                  {({ active }) => (
                    <li
                      className={classNames(
                        'flex items-center justify-between px-4 py-2 cursor-pointer',
                        active ? 'bg-gray-100' : ''
                      )}
                    >
                      <div className='flex items-center gap-2'>
                        <input
                          type='checkbox'
                          checked={selected.includes(option)}
                          readOnly
                        />
                        <span className='text-sm text-gray-800'>{option}</span>
                      </div>
                      {selected.includes(option) && (
                        <Check className='h-4 w-4 text-green-500' />
                      )}
                    </li>
                  )}
                </Listbox.Option>
              ))}
            </div>
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
};

const RenderMultiSelect = (
  label: string,
  options: string[],
  selected: string[],
  setSelected: React.Dispatch<React.SetStateAction<string[]>>,
  allowCustomField = false
) => (
  <MultiSelectInner
    label={label}
    options={options}
    selected={selected}
    setSelected={setSelected}
    allowCustomField={allowCustomField}
  />
);

export default RenderMultiSelect;
